import type React from 'react';
import { Link } from 'react-router-dom';

import { Typography } from '@workspace/ui/components/Typography';
import { useBreakpoint } from '@workspace/ui/hooks/useBreakpoint';
import { cn } from '@workspace/ui/lib/utils';

import { createCategoryUrl } from '~/components/CategoryBlocks/utils';
import type { Category } from '~/stores/CategoriesStore';

import { CategoryBlockBackgroundImage } from './shared/CategoryBlockBackgroundImage';
import {
  BLOCK_SIZES_DESKTOP,
  BLOCK_WIDTHS_DESKTOP,
  BLOCK_WIDTHS_MOBILE,
} from './shared/CategoryBlockBackgroundImage/constants';
import { CategoryBlockHeader } from './shared/CategoryBlockHeader';

interface CategoriesPromoProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> {
  category: Category;
  priority?: boolean;
}

/**
 * `sizes` for the three narrow cards that share a row below the wide first one:
 * a third of the row minus the two `gap-4` gutters.
 *
 * Derived from the viewport rather than pinned to 456px, because that figure is
 * only true once the row hits the 1400px cap. This string is the `sizes` of a
 * `(min-width: 768px)` source, so at 768px the real card is about 218px — a
 * flat 456px would tell the browser to fetch twice the width, four times the
 * pixels.
 */
const CARD_SIZES_DESKTOP =
  '(min-width: 1432px) 456px, calc((100vw - 64px) / 3)';

/**
 * `sizes` for a card while it is still full width, below the `md` breakpoint at
 * which the row splits.
 */
const CARD_SIZES_MOBILE = 'calc(100vw - 32px)';

/**
 * Ratios of the boxes the layout reserves, mirroring the `aspect-*` classes.
 * The first card is a wide hero on desktop and a tall panel on phones; the rest
 * are shallow strips with a title card overlapping from below.
 */
const mobileAspectRatio = (index: number) =>
  index === 0 ? 328 / 381 : 328 / 146;
const desktopAspectRatio = (index: number) =>
  index === 0 ? 1400 / 370 : 456 / 146;

export const CategoriesPromo: React.FC<CategoriesPromoProps> = ({
  category,
  className,
  priority = false,
  ...props
}) => {
  const { isMobile } = useBreakpoint();

  const categories = category.subcategory.slice(0, 4);

  return (
    <div
      {...props}
      className={cn('mt-6 flex flex-col gap-6 md:gap-8', className)}
    >
      <CategoryBlockHeader
        category={category}
        showViewAll={!isMobile}
        isCategoriesBlock
      />

      <div className="flex gap-4 max-md:flex-col md:flex-wrap">
        {categories.map((category, index) => (
          <CategoryPromoCard
            key={category.id}
            category={category}
            index={index}
            priority={priority && index === 0}
          />
        ))}
      </div>
    </div>
  );
};

interface CategoryPromoCardProps
  extends Omit<React.ComponentPropsWithoutRef<typeof Link>, 'to' | 'children'> {
  category: Category;
  index: number;
  priority?: boolean;
}

/**
 * One promo card.
 *
 * The first card is art-directed: a tall panel fed by `previewImageThumb` on
 * phones and a wide hero fed by `previewImage` on desktop. Both variants go to
 * the browser as a `<picture>` rather than being chosen here, because
 * `useBreakpoint` reports a width of 0 until after mount — picking in
 * JavaScript would make every desktop first paint request the portrait crop of
 * the mobile thumbnail and then download the real hero on top of it.
 */
const CategoryPromoCard: React.FC<CategoryPromoCardProps> = ({
  category,
  className,
  index,
  priority = false,
  ...props
}) => {
  const previewImage = category.subcategory[0]?.previewImage || '';
  const previewImageThumb = category.subcategory[0]?.previewImageThumb || '';

  const isHero = index === 0;

  // `Category` normalises a missing image to an empty string rather than
  // leaving it undefined, so these fall back with `||`. With `??` a category
  // that carries only `previewImage` would keep the empty thumb and the card
  // would render no image at all.
  const mobileSrc = (isHero && previewImageThumb) || previewImage;
  const desktopSrc = previewImage || mobileSrc;

  return (
    <Link
      {...props}
      to={createCategoryUrl({ id: category.id })}
      className={cn(
        'flex flex-col',
        index === 0
          ? 'aspect-328/381 rounded-6.5 md:aspect-1400/370 md:basis-full'
          : 'aspect-328/202 rounded-6 md:aspect-456/202 md:grow md:basis-[1%]',
        className,
      )}
    >
      {mobileSrc && (
        <div
          className={cn(
            'z-1 w-full overflow-hidden rounded-[inherit]',
            isHero
              ? 'aspect-328/381 md:aspect-1400/370'
              : 'aspect-328/146 md:aspect-456/146',
          )}
        >
          <CategoryBlockBackgroundImage
            priority={priority}
            mobile={{
              src: mobileSrc,
              aspectRatio: mobileAspectRatio(index),
              sizes: CARD_SIZES_MOBILE,
              widths: BLOCK_WIDTHS_MOBILE,
            }}
            desktop={{
              src: desktopSrc,
              aspectRatio: desktopAspectRatio(index),
              sizes: isHero ? BLOCK_SIZES_DESKTOP : CARD_SIZES_DESKTOP,
              widths: isHero ? BLOCK_WIDTHS_DESKTOP : BLOCK_WIDTHS_MOBILE,
            }}
          />
        </div>
      )}
      {index !== 0 && (
        <div className="-mt-10 flex flex-1 items-center justify-center rounded-[inherit] border border-stroke-secondary px-2 pt-12 pb-4">
          <Typography.Text200Regular className="text-center text-text-primary">
            {category.name}
          </Typography.Text200Regular>
        </div>
      )}
    </Link>
  );
};
